import React, { useState, useMemo, useEffect } from 'react';

export default function AnalyticsView({ invoices = [], activeContracts = [], employees = [], user }) {
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState('All Months');
  const [selectedConsultant, setSelectedConsultant] = useState('All Consultants');
  
  // Login Duration State
  const [loginStats, setLoginStats] = useState({ Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0 });

  const months = useMemo(() => ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"], []);
  const years = [2024, 2025, 2026, 2027];

  // --- 0. EXTRACT UNIQUE CONSULTANTS ---
  const uniqueConsultants = useMemo(() => {
    const names = invoices.map(inv => inv.consultantName).filter(n => n);
    return [...new Set(names)].sort();
  }, [invoices]);

  // --- 1. FILTER DATA ---
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      const yearMatch = inv.year === selectedYear;
      const monthMatch = selectedMonth === 'All Months' || inv.month === selectedMonth;
      const consultantMatch = selectedConsultant === 'All Consultants' || inv.consultantName === selectedConsultant;
      return yearMatch && monthMatch && consultantMatch; 
    });
  }, [invoices, selectedYear, selectedMonth, selectedConsultant]);

  // --- 2. CALCULATE FINANCIAL METRICS ---
  const metrics = useMemo(() => {
    let totalRevenue = 0;
    let totalCost = 0;
    
    const monthlyData = Array(12).fill(0).map(() => ({ revenue: 0, cost: 0, profit: 0 }));
    const clientData = {};

    filteredInvoices.forEach(inv => {
      let revenue = 0;
      let cost = 0;

      if (inv.calculatedAmount !== undefined) {
        revenue = parseFloat(inv.calculatedAmount);
        cost = parseFloat(inv.calculatedCost || 0);
      } else if (inv.totalHours !== undefined && inv.totalHours !== null) {
        revenue = (parseFloat(inv.dayRate || 0) / 8) * parseFloat(inv.totalHours);
        cost = (parseFloat(inv.costRate || 0) / 8) * parseFloat(inv.totalHours);
      } else {
        revenue = (parseFloat(inv.dayRate || 0) * 20);
        cost = (parseFloat(inv.costRate || 0) * 20);
      }

      if (inv.consultantStatus === 'Early Payment Requested' && inv.earlyPayDiscount) {
        const discount = parseFloat(inv.earlyPayDiscount) / 100;
        cost = cost * (1 - discount);
      }

      totalRevenue += revenue;
      totalCost += cost;

      const mIdx = months.indexOf(inv.month);
      if (mIdx >= 0) {
        monthlyData[mIdx].revenue += revenue;
        monthlyData[mIdx].cost += cost;
        monthlyData[mIdx].profit += (revenue - cost);
      }

      const client = inv.clientName || 'Unknown';
      if (!clientData[client]) clientData[client] = 0;
      clientData[client] += (revenue - cost);
    });

    return {
      revenue: totalRevenue,
      cost: totalCost,
      profit: totalRevenue - totalCost,
      margin: totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue) * 100 : 0,
      monthly: monthlyData,
      byClient: Object.entries(clientData).sort((a,b) => b[1] - a[1]) 
    };
  }, [filteredInvoices, months]);

  // --- 3. EMPLOYEE PROFITABILITY ---
  const employeePerformance = useMemo(() => {
      return employees.map(emp => {
          // Attempt to find contracts managed by this employee (checking multiple fields for robustness)
          const empContracts = activeContracts.filter(c => c.recruiterId === emp.id || c.accountManagerId === emp.id);
          
          let revenue = 0;
          let profit = 0;

          // If we have contracts, sum up their invoices for the selected year
          empContracts.forEach(c => {
             const cInvoices = invoices.filter(i => i.contractId === c.id && i.year === selectedYear);
             cInvoices.forEach(inv => {
                 let invRevenue = 0;
                 let invCost = 0;
                 
                 if (inv.calculatedAmount) {
                     invRevenue = parseFloat(inv.calculatedAmount);
                     invCost = parseFloat(inv.calculatedCost || 0);
                 } else {
                     invRevenue = parseFloat(inv.dayRate || 0) * 20; 
                     invCost = parseFloat(inv.costRate || 0) * 20;
                 }
                 
                 revenue += invRevenue;
                 profit += (invRevenue - invCost);
             });
          });

          return { 
              name: emp.name, 
              image: emp.imageFile, 
              activeContracts: empContracts.length, 
              revenue, 
              profit 
          };
      }).sort((a,b) => b.profit - a.profit);
  }, [employees, activeContracts, invoices, selectedYear]);


  // --- 4. FETCH LOGIN STATS (REAL OR MOCK) ---
  useEffect(() => {
      const fetchLoginStats = async () => {
          if (!user) return;
          
          try {
              // MOCK DATA for "Average Daily Login Duration (Hours)"
              // In production, this would query a 'sessions' collection or audit logs.
              // Simulating realistic working hours with variance.
              const mockStats = {
                  Mon: (7 + Math.random() * 2),
                  Tue: (8 + Math.random()),
                  Wed: (7.5 + Math.random()),
                  Thu: (8 + Math.random()),
                  Fri: (6 + Math.random())
              };
              setLoginStats(mockStats);
          } catch (e) {
              console.error(e);
          }
      };
      fetchLoginStats();
  }, [user]);

  // Helpers
  const maxMonthlyVal = Math.max(...metrics.monthly.map(m => Math.max(m.revenue, m.cost)), 1);
  const maxClientVal = Math.max(...metrics.byClient.map(c => c[1]), 1);

  return (
    <div className="container-fluid p-0">
      {/* HEADER & FILTERS */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h2 className="fw-bold text-main">Financial Analytics</h2>
          <p className="text-muted-theme">Insights based on generated invoices.</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <select className="form-select w-auto" value={selectedConsultant} onChange={e => setSelectedConsultant(e.target.value)}>
            <option value="All Consultants">All Consultants</option>
            {uniqueConsultants.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="form-select w-auto" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
            <option value="All Months">Whole Year</option>
            {months.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="form-select w-auto" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
            {years.map(y => <option key={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* TOP ROW: LOGIN STATS & EMPLOYEE PROFITABILITY */}
      <div className="row g-4 mb-4">
          {/* LOGIN DURATION WIDGET */}
          <div className="col-lg-5">
              <div className="card h-100 bg-card border-theme shadow-sm">
                  <div className="card-header bg-transparent border-theme">
                      <h6 className="mb-0 text-main"><i className="bi bi-clock-history me-2 text-info"></i> Avg. Login Duration (This Week)</h6>
                  </div>
                  <div className="card-body d-flex align-items-end justify-content-between px-4 pb-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(day => (
                          <div key={day} className="d-flex flex-column align-items-center" style={{height: '150px', width: '15%'}}>
                              <div className="text-main fw-bold small mb-1">{loginStats[day].toFixed(1)}h</div>
                              <div className="w-100 bg-input rounded-top position-relative flex-grow-1">
                                  <div 
                                      className="position-absolute bottom-0 start-0 w-100 bg-info rounded-top opacity-75" 
                                      style={{height: `${(loginStats[day] / 12) * 100}%`, transition: 'height 1s'}}
                                  ></div>
                              </div>
                              <div className="text-muted-theme small mt-2">{day}</div>
                          </div>
                      ))}
                  </div>
              </div>
          </div>

          {/* EMPLOYEE PROFITABILITY WIDGET */}
          <div className="col-lg-7">
              <div className="card h-100 bg-card border-theme shadow-sm">
                  <div className="card-header bg-transparent border-theme">
                      <h6 className="mb-0 text-main"><i className="bi bi-people me-2 text-primary"></i> Profitability per Employee (YTD)</h6>
                  </div>
                  <div className="card-body p-0 overflow-auto" style={{maxHeight: '220px'}}>
                      <table className="table table-hover mb-0 text-main align-middle">
                          <thead className="bg-input sticky-top">
                              <tr>
                                  <th className="bg-input border-theme small text-muted-theme ps-3">Employee</th>
                                  <th className="bg-input border-theme small text-muted-theme text-center">Active Contracts</th>
                                  <th className="bg-input border-theme small text-muted-theme text-end pe-3">Est. Profit</th>
                              </tr>
                          </thead>
                          <tbody>
                              {employeePerformance.map((emp, idx) => (
                                  <tr key={idx}>
                                      <td className="ps-3">
                                          <div className="d-flex align-items-center gap-2">
                                              {emp.image ? (
                                                  <img src={emp.image} className="rounded-circle" style={{width:24,height:24}} alt={emp.name}/>
                                              ) : (
                                                  <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white small" style={{width:24,height:24}}>
                                                      {emp.name.charAt(0)}
                                                  </div>
                                              )}
                                              <span className="small">{emp.name}</span>
                                          </div>
                                      </td>
                                      <td className="text-center small">{emp.activeContracts}</td>
                                      <td className="text-end pe-3 small fw-bold text-success">
                                          €{emp.profit.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                      </td>
                                  </tr>
                              ))}
                              {employeePerformance.length === 0 && (
                                  <tr><td colSpan="3" className="text-center text-muted-theme small py-3">No employees found.</td></tr>
                              )}
                          </tbody>
                      </table>
                  </div>
              </div>
          </div>
      </div>

      {/* KPI CARDS */}
      <div className="row g-4 mb-4">
        <KpiCard 
          title="Total Revenue" 
          value={`€${metrics.revenue.toLocaleString(undefined, {maximumFractionDigits: 0})}`} 
          icon="bi-wallet2" 
          color="text-primary" 
        />
        <KpiCard 
          title="Total Cost" 
          value={`€${metrics.cost.toLocaleString(undefined, {maximumFractionDigits: 0})}`} 
          icon="bi-cash-stack" 
          color="text-danger" 
        />
        <KpiCard 
          title="Gross Profit" 
          value={`€${metrics.profit.toLocaleString(undefined, {maximumFractionDigits: 0})}`} 
          icon="bi-graph-up-arrow" 
          color="text-success" 
        />
        <KpiCard 
          title="Avg. Margin" 
          value={`${metrics.margin.toFixed(1)}%`} 
          icon="bi-pie-chart" 
          color="text-info" 
        />
      </div>

      <div className="row g-4">
        {/* CHART SECTION */}
        <div className="col-lg-8">
          <div className="card h-100 bg-card border-theme shadow-sm">
            <div className="card-header bg-transparent border-theme">
              <h5 className="mb-0 text-main">
                {selectedMonth === 'All Months' ? 'Monthly Performance' : `Client Breakdown (${selectedMonth})`}
              </h5>
            </div>
            <div className="card-body">
              {/* YEARLY VIEW: BAR CHART */}
              {selectedMonth === 'All Months' ? (
                <div className="d-flex justify-content-between align-items-end h-100 px-2 pb-2" style={{minHeight: '300px'}}>
                  {metrics.monthly.map((m, i) => (
                    <div key={i} className="d-flex flex-column justify-content-end align-items-center flex-grow-1" style={{height: '100%'}}>
                      {/* Bars Container */}
                      <div className="d-flex align-items-end gap-1" style={{height: '85%', width: '60%'}}>
                        {/* Revenue Bar (Blue) */}
                        <div 
                          className="bg-primary opacity-75 rounded-top w-50" 
                          style={{height: `${(m.revenue / maxMonthlyVal) * 100}%`, transition: 'height 0.5s'}}
                          title={`Rev: €${m.revenue.toLocaleString()}`}
                        ></div>
                        {/* Profit Bar (Green) - Visualizing net profit */}
                        <div 
                          className="bg-success opacity-75 rounded-top w-50" 
                          style={{height: `${(m.profit / maxMonthlyVal) * 100}%`, transition: 'height 0.5s'}}
                          title={`Profit: €${m.profit.toLocaleString()}`}
                        ></div>
                      </div>
                      {/* Label */}
                      <div className="text-muted-theme small mt-2 text-truncate" style={{maxWidth: '40px'}}>{months[i].substring(0,3)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                /* MONTHLY VIEW: HORIZONTAL BARS BY CLIENT */
                <div className="d-flex flex-column justify-content-center h-100" style={{minHeight: '300px'}}>
                  {metrics.byClient.length > 0 ? metrics.byClient.map(([client, profit]) => (
                    <div key={client} className="mb-3">
                      <div className="d-flex justify-content-between text-main small mb-1">
                        <span>{client}</span>
                        <span className="fw-bold text-success">€{profit.toLocaleString()}</span>
                      </div>
                      <div className="progress bg-input" style={{height: '10px'}}>
                        <div 
                          className="progress-bar bg-success" 
                          role="progressbar" 
                          style={{width: `${(profit / maxClientVal) * 100}%`}}
                        ></div>
                      </div>
                    </div>
                  )) : (
                    <p className="text-center text-muted-theme">No invoice data for this month.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* DETAILS PANEL */}
        <div className="col-lg-4">
          <div className="card h-100 bg-card border-theme shadow-sm">
            <div className="card-header bg-transparent border-theme">
              <h5 className="mb-0 text-main">Summary</h5>
            </div>
            <div className="card-body">
              <ul className="list-group list-group-flush">
                <li className="list-group-item bg-transparent border-theme d-flex justify-content-between text-main">
                  <span>Invoices Count</span>
                  <span className="fw-bold">{filteredInvoices.length}</span>
                </li>
                <li className="list-group-item bg-transparent border-theme d-flex justify-content-between text-main">
                  <span>Avg. Revenue/Invoice</span>
                  <span className="fw-bold">
                    €{filteredInvoices.length ? (metrics.revenue / filteredInvoices.length).toLocaleString(undefined, {maximumFractionDigits:0}) : 0}
                  </span>
                </li>
                {selectedMonth === 'All Months' && (
                   <li className="list-group-item bg-transparent border-theme d-flex justify-content-between text-main">
                     <span>Best Month</span>
                     <span className="fw-bold text-success">
                        {months[metrics.monthly.reduce((iMax, x, i, arr) => x.revenue > arr[iMax].revenue ? i : iMax, 0)]}
                     </span>
                   </li>
                )}
              </ul>
              
              <div className="mt-4 p-3 bg-input rounded border border-theme">
                  <h6 className="text-muted-theme small text-uppercase fw-bold mb-3">Legend</h6>
                  <div className="d-flex align-items-center mb-2">
                      <div className="rounded bg-primary opacity-75" style={{width: '15px', height: '15px'}}></div>
                      <span className="text-main small ms-2">Revenue</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                      <div className="rounded bg-danger opacity-75" style={{width: '15px', height: '15px'}}></div>
                      <span className="text-main small ms-2">Cost (Consultant Pay)</span>
                  </div>
                  <div className="d-flex align-items-center">
                      <div className="rounded bg-success opacity-75" style={{width: '15px', height: '15px'}}></div>
                      <span className="text-main small ms-2">Net Profit</span>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const KpiCard = ({ title, value, icon, color }) => (
  <div className="col-md-6 col-xl-3">
    <div className="card h-100 bg-card border-theme shadow-sm">
      <div className="card-body d-flex align-items-center">
        <div className={`rounded-circle bg-body border border-theme p-3 me-3 d-flex align-items-center justify-content-center ${color}`} style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}>
            <i className={`bi ${icon}`}></i>
        </div>
        <div>
            <h6 className="text-muted-theme mb-0 small">{title}</h6>
            <h4 className="fw-bold mb-0 text-main">{value}</h4>
        </div>
      </div>
    </div>
  </div>
);